import type { parsedApiError } from "@/types/api/errors.api";
import type { user } from "@/types/api/user.api";
import { validate } from "@/utils/validators";
import { useCallback, useEffect, useState } from "react";
import type { userFormData } from "./types.props";
import { userSchema } from "./user.schema";

export const initialFormState: userFormData = {
  identifier: "",
  first_name: "",
  second_name: "",
  last_name: "",
  second_last_name: "",
  email: "",
  password: "",
  status: "active",
  role: "Estudiante",
  institution: { id: "", name: "" },
};

export const useUserForm = (initialData?: user) => {
  const [formData, setFormData] = useState<userFormData>(initialFormState);
  const [formErrors, setFormErrors] = useState<parsedApiError | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const isEditMode = Boolean(initialData);

  useEffect(() => {
    if (initialData) {
      setFormData({
        identifier: initialData.identifier || "",
        first_name: initialData.first_name,
        second_name: initialData.second_name || "",
        last_name: initialData.last_name || "",
        second_last_name: initialData.second_last_name || "",
        email: initialData.email || "",
        status: initialData.status || "active",
        role: initialData.role || "Estudiante",
        institution: initialData.institution
          ? {
              id: initialData.institution.id,
              name: initialData.institution.name,
            }
          : { id: "", name: "" },
        password: "",
      });
    } else {
      setFormData(initialFormState);
    }
  }, [initialData]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormErrors(null);

      setFormData((prev) => {
        const newState = { ...prev };

        if (name === "institution") {
          newState.institution = { id: "", name: value };
        } else if (name === "role" && value === "Facilitador") {
          newState.role = value;
          newState.institution = { id: "", name: "" };
        } else {
          (newState as any)[name] = value;
        }

        return newState;
      });
    },
    []
  );

  const validateForm = useCallback(() => {
    setFormErrors(null);
    const validationErrors = validate(formData, userSchema(isEditMode));
    if (Object.keys(validationErrors).length > 0) {
      const errorPayload: parsedApiError = {
        generalMessage: "Por favor, corrija los errores del formulario.",
        fieldErrors: validationErrors,
        code: "client_validation_error",
      };
      setFormErrors(errorPayload);
      return false;
    }
    return true;
  }, [formData, isEditMode]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  return {
    formData,
    formErrors,
    setFormErrors,
    isEditMode,
    showPassword,
    handleInputChange,
    validateForm,
    togglePasswordVisibility,
  };
};
